/**
 * @fileoverview Styles for Add Students Modal
 * @description Apple HIG compliant styles for modal (desktop) and bottom sheet (mobile)
 * @see https://developer.apple.com/design/human-interface-guidelines/sheets
 */

import { createStyles, rem } from 'hooks'
import type { CSSObject } from '@mantine/core'

const SPACING = { xs: 4, sm: 8, md: 16, lg: 20, xl: 32 }
const FOOTER_HEIGHT = 72
const GRABBER = { width: 36, height: 5, radius: 2.5 }
const SHEET_RADIUS = 10
const MIN_TOUCH_TARGET = 44

export const mobileDrawerStyles: Record<string, CSSObject> = {
  header: { display: 'none' },
  body: { padding: 0, display: 'flex', flexDirection: 'column' },
  content: {
    borderTopLeftRadius: SHEET_RADIUS,
    borderTopRightRadius: SHEET_RADIUS,
    height: 'auto',
    maxHeight: '90vh',
  },
  inner: { height: 'auto' },
  overlay: { backgroundColor: 'rgba(0, 0, 0, 0.4)' },
}

export const useStyles = createStyles(theme => ({
  modal: {
    '.mantine-Modal-content': {
      width: '90vw',
      maxWidth: '80vw',
      height: 'auto',
      maxHeight: '85vh',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: rem(12),
    },
    '.mantine-Modal-header': {
      padding: `${rem(SPACING.md)} ${rem(SPACING.lg)}`,
      borderBottom: `1px solid ${theme.colors.gray[2]}`,
    },
    '.mantine-Modal-title': {
      fontSize: rem(17),
      fontWeight: 600,
    },
    '.mantine-Modal-body': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      padding: 0,
      overflow: 'hidden',
    },
  },

  drawer: {
    '.mantine-Drawer-content': {
      borderTopLeftRadius: rem(SHEET_RADIUS),
      borderTopRightRadius: rem(SHEET_RADIUS),
      boxShadow: '0 -1px 3px rgba(0, 0, 0, 0.08), 0 -4px 16px rgba(0, 0, 0, 0.08)',
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
    },
  },

  dragHandle: {
    width: rem(GRABBER.width),
    height: rem(GRABBER.height),
    backgroundColor: theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(60, 60, 67, 0.3)',
    borderRadius: rem(GRABBER.radius),
    margin: `${rem(6)} auto ${rem(SPACING.sm)}`,
    flexShrink: 0,
  },

  drawerContent: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    paddingTop: rem(SPACING.sm),
  },

  tabsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },

  tabPanel: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: `${rem(SPACING.lg)} ${rem(SPACING.xl)}`,
    minHeight: rem(200),
    scrollBehavior: 'smooth',
    WebkitOverflowScrolling: 'touch',
  },

  footer: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: `${rem(SPACING.md)} ${rem(SPACING.lg)}`,
    gap: rem(SPACING.sm),
    minHeight: rem(FOOTER_HEIGHT),
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
    borderTop: `1px solid ${theme.colors.gray[2]}`,
    '& button': {
      minHeight: rem(MIN_TOUCH_TARGET),
      paddingLeft: rem(SPACING.lg),
      paddingRight: rem(SPACING.lg),
    },
  },

  inviteCodeBox: {
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
    borderRadius: rem(8),
    padding: `${rem(SPACING.sm)} ${rem(SPACING.md)}`,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: rem(2),
  },

  inviteCode: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
    letterSpacing: '0.12em',
    fontSize: rem(28),
    fontWeight: 600,
    color: theme.colorScheme === 'dark' ? theme.white : theme.colors.dark[7],
    [`@media (max-width: ${theme.breakpoints.sm})`]: {
      fontSize: rem(22),
      letterSpacing: '0.08em',
    },
  },

  studentsContainer: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollBehavior: 'smooth',
    WebkitOverflowScrolling: 'touch',
  },
}))
